import { CardCarousel, CardDetailsActions, CardSummaryTransition } from '@mobile/screens/card-details/card-carousel'
import { CardBalanceActions, CardHeader } from '@mobile/screens/card-details/card-summary'
import { useCardSheet } from '@mobile/screens/card-details/use-card-sheet'
import { ScreenCardInfo } from '@mobile/screens/card-info'
import { HomeOperations } from '@mobile/screens/home/operations'
import {
  type BackendCard,
  type CardTemplateId,
  isBlockedCardStatus,
  isFrozenCardStatus,
  isIssuedCard,
  matchesCardTemplate,
  selectedCardIssue,
} from '@shared-core/features/backend'
import { useCardCredentials } from '@shared-core/hooks/use-card-credentials'
import { useCardDetails } from '@shared-core/hooks/use-card-details'
import { useCardFreeze } from '@shared-core/hooks/use-card-freeze'
import { useCardSelection } from '@shared-core/hooks/use-card-selection'
import { useCardTemplates } from '@shared-core/hooks/use-card-templates'
import { useCards } from '@shared-core/hooks/use-cards'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { type RootRouteProps, ScreensList, useNavigation, useRoute } from '@shared-core/navigation'
import { useAppSelector } from '@shared-core/store'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { AppCardBlockSheet } from '@shared-ui/components/app/card-block-sheet'
import blockLocales from '@shared-ui/components/app/card-block-sheet/index.i18n.json'
import { AppCardFreezeSheet } from '@shared-ui/components/app/card-freeze-sheet'
import freezeLocales from '@shared-ui/components/app/card-freeze-sheet/index.i18n.json'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { CardDetailsField } from '@shared-ui/components/ui/card-details-field'
import { Column, Flex, Row } from '@shared-ui/components/ui/grid'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { Skeleton, SkeletonBlock } from '@shared-ui/components/ui/skeleton'
import { GText } from '@shared-ui/components/ui/text'
import { useBalanceHider } from '@shared-ui/hooks/ui/use-balance-hider'
import { rh, rw } from '@shared-ui/theme/scale'
import { type ComponentRef, useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Pressable, ScrollView } from 'react-native'
import Reanimated, { Easing, FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

const CARD_CONTENT_ENTERING = FadeInDown.duration(280)
  .easing(Easing.out(Easing.cubic))
  .withInitialValues({ opacity: 0, transform: [{ translateY: rh(12) }] })

const CREDENTIAL_ENTERING = FadeInDown.duration(220)
  .easing(Easing.out(Easing.cubic))
  .withInitialValues({ opacity: 0, transform: [{ translateY: rh(4) }] })

const AnimatedPanelPressable = Animated.createAnimatedComponent(Pressable)

type CredentialProps = { label: string; value: string; isCopyVisible: boolean; isLoading: boolean }

function Credential({ label, value, isCopyVisible, isLoading }: CredentialProps) {
  if (isLoading) {
    return (
      <Skeleton>
        <SkeletonBlock width="100%" height={rh(64)} radius={rw(16)} />
      </Skeleton>
    )
  }

  return (
    <Reanimated.View
      key={isCopyVisible ? 'visible' : 'hidden'}
      entering={isCopyVisible ? CREDENTIAL_ENTERING : undefined}
    >
      <CardDetailsField label={label} value={value} isCopyVisible={isCopyVisible} />
    </Reanimated.View>
  )
}

type CardRequisitesProps = {
  cardId: string
  maskedPan: string
  billingAddress: BackendCard['billingAddress']
}

function CardRequisites({ cardId, maskedPan, billingAddress }: CardRequisitesProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const navigation = useNavigation()

  const { credentials, isHidden, isLoading, toggleHidden } = useCardCredentials(cardId)
  const lastDigits = useMemo(() => maskedPan.replace(/ /g, '').slice(-4), [maskedPan])

  return (
    <Column gap={16}>
      <Row align="center" justify="space-between">
        <GText fw={600} style={styles.title}>
          {t('requisites')}
        </GText>
        <Pressable accessibilityRole="button" onPress={toggleHidden}>
          <GText fw={500} style={styles.link}>
            {t(isHidden ? 'show' : 'hide')}
          </GText>
        </Pressable>
      </Row>
      <Column gap={12}>
        <Credential
          label={t('number')}
          value={isHidden ? `•••• ${lastDigits}` : (credentials?.number ?? '')}
          isCopyVisible={!isHidden}
          isLoading={isLoading}
        />
        <Row gap={12}>
          <Flex>
            <Credential
              label={t('expiry')}
              value={isHidden ? '••/••' : (credentials?.expiry ?? '')}
              isCopyVisible={!isHidden}
              isLoading={isLoading}
            />
          </Flex>
          <Flex>
            <Credential
              label={t('cvc')}
              value={isHidden ? '•••' : (credentials?.cvc ?? '')}
              isCopyVisible={!isHidden}
              isLoading={isLoading}
            />
          </Flex>
        </Row>
        {billingAddress && (
          <Pressable
            accessibilityRole="button"
            onPress={() =>
              navigation.navigate(ScreensList.FORM_SHEET_BILLING_ADDRESS, {
                address: {
                  country: billingAddress.country,
                  address: billingAddress.address,
                  city: billingAddress.city,
                  region: billingAddress.region ?? '—',
                  'postal-code': billingAddress.postalCode ?? '—',
                },
              })
            }
            style={[styles.credential, styles.billingRow]}
          >
            <GText fw={500} size={16}>
              {t('billing')}
            </GText>
            <AppIcon icon="chevron-down" size={20} isSecondary />
          </Pressable>
        )}
      </Column>
    </Column>
  )
}

type DetailContentParams = {
  cardId?: string
  isBlocked: boolean
  cardType: CardTemplateId
  maskedPan: string
  billingAddress: BackendCard['billingAddress']
  numberLabel: string
  styles: ReturnType<typeof useCreateStyles>
}

function renderDetailContent({
  cardId,
  isBlocked,
  cardType,
  maskedPan,
  billingAddress,
  numberLabel,
  styles,
}: DetailContentParams) {
  if (!cardId) return isBlocked ? null : <ScreenCardInfo embedded cardType={cardType} />

  if (isBlocked)
    return <CardDetailsField label={numberLabel} value={maskedPan} isCopyVisible={false} style={styles.blockedNumber} />

  return <CardRequisites key={cardId} cardId={cardId} maskedPan={maskedPan} billingAddress={billingAddress} />
}

// Обложка выбранной карты сохраняется при раскрытии и скрытии панели.
type ScreenCardDetailsProps = { isHome?: boolean; initialCardId?: string; initialTemplateId?: CardTemplateId }

export function ScreenCardDetails({ isHome = false, initialCardId, initialTemplateId }: ScreenCardDetailsProps = {}) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const route = useRoute<RootRouteProps<ScreensList.CARD_DETAILS>>()
  const navigation = useNavigation()
  const carouselRef = useRef<ComponentRef<typeof ScrollView>>(null)
  const contentScrollRef = useRef<ComponentRef<typeof ScrollView>>(null)
  const templates = useCardTemplates()
  const cards = useCards()
  const cardIssue = useAppSelector(selectedCardIssue)
  const { t: blockText } = useI18N(blockLocales)
  const { t: freezeText } = useI18N(freezeLocales)
  const [blockSheetVisible, setBlockSheetVisible] = useState(false)
  const templateId = initialTemplateId ?? route.params?.templateId ?? 'platinum'
  const { selectedType, setSelectedType, selectCard, selectedCard } = useCardSelection(
    route.params?.selectedCardType ?? templateId,
  )
  const routeSelectedType = route.params?.selectedCardType

  // Возврат на главную после пополнения выбирает пополненную карту.
  useEffect(() => {
    if (routeSelectedType) setSelectedType(routeSelectedType)
  }, [routeSelectedType, setSelectedType])
  const cardId = selectedCard?.id ?? (selectedType === templateId ? (initialCardId ?? route.params?.cardId) : undefined)
  const loadedCard = useCardDetails(cardId)
  const cardStatus = selectedCard?.status ?? loadedCard?.status
  const isBlocked = isBlockedCardStatus(cardStatus)
  const isFrozen = isFrozenCardStatus(cardStatus)
  const freezeFlow = useCardFreeze(cardId, cardStatus)
  const [freezeSheetVisible, setFreezeSheetVisible] = useState(false)
  useEffect(() => {
    setFreezeSheetVisible(false)
  }, [cardId])
  const maskedPan = loadedCard?.maskedPan ?? ''
  const cardBalance = loadedCard?.balance ?? '0'
  const billingAddress = loadedCard?.billingAddress ?? null
  const [bodyHeight, setBodyHeight] = useState(0)
  const issuedCards = useMemo(() => cards?.filter(isIssuedCard) ?? [], [cards])
  const returnResolveRef = useRef<(() => void) | null>(null)
  const finishCardReturn = useEvent(() => {
    returnResolveRef.current?.()
    returnResolveRef.current = null
  })
  const returnToIssuedCard = useEvent(() => {
    contentScrollRef.current?.scrollTo({ y: 0, animated: true })
    const hasSelectedCard = issuedCards.some((card) => matchesCardTemplate(card, selectedType))

    if (!hasSelectedCard && issuedCards.length > 0) {
      finishCardReturn()
      return new Promise<void>((resolve) => {
        returnResolveRef.current = resolve
        carouselRef.current?.scrollTo({ x: 0, animated: true })
      })
    }
  })
  const { isDetails, progress, showDetails, gestures, panelTop, historyOpacity, detailsOpacity } = useCardSheet(
    bodyHeight,
    undefined,
    returnToIssuedCard,
  )
  const lastDigits = useMemo(() => {
    const pan = selectedCard?.maskedPan ?? maskedPan
    return pan.replace(/ /g, '').slice(-4)
  }, [selectedCard, maskedPan])
  // Возвращаем обложку выпущенной карты при закрытии описания невыпущенной.
  const { isBalanceShow, toggleGlobal } = useBalanceHider()
  const currentBalance = selectedCard?.balance ?? cardBalance
  const balance = isBalanceShow ? `$${formatWithThousandSeparators(currentBalance).replace('.', ',')}` : '••••'
  const balanceKey = `${selectedType}:${isBalanceShow}:${balance}`
  const closeDetails = () => showDetails(false)
  const detailContent = renderDetailContent({
    cardId,
    isBlocked,
    cardType: selectedType,
    maskedPan: selectedCard?.maskedPan ?? maskedPan,
    billingAddress,
    numberLabel: t('number'),
    styles,
  })
  // В истории сохраняем карты, выпуск которых уже начинался, включая неудачные попытки.
  const cardTypes = useMemo(() => {
    const types = [
      ...new Set([
        templateId,
        ...templates.map((template) => template.id),
        ...(cards ?? []).filter(isIssuedCard).map((card) => card.templateId),
      ]),
    ]

    if (isDetails) return types

    return types.filter(
      (type) =>
        type === templateId ||
        cardIssue.invoice?.templateId === type ||
        cards?.some((item) => matchesCardTemplate(item, type)),
    )
  }, [templateId, templates, isDetails, cards, cardIssue.invoice])
  // После скрытия невыпущенной карты возвращаем карусель к доступной обложке.
  useEffect(() => {
    if (!isDetails && !cardTypes.some((type) => type === selectedType)) {
      setSelectedType(templateId)
      carouselRef.current?.scrollTo({ x: 0, animated: true })
    }
  }, [cardTypes, isDetails, selectedType, templateId])

  const openCard = useEvent((type: CardTemplateId) => {
    selectCard(type)

    if (isBlocked && type === selectedType) setBlockSheetVisible(true)
    else showDetails(true)
  })

  return (
    <ScreenWrapperWithoutScroll style={styles.root}>
      <CardHeader
        balance={balance}
        balanceKey={balanceKey}
        sheet={{ detailsOpacity, panelTop }}
        isHome={isHome && !isDetails}
        onBack={isDetails ? closeDetails : undefined}
        onToggleBalance={toggleGlobal}
      />
      <Flex style={styles.body} onLayout={(event) => setBodyHeight(event.nativeEvent.layout.height)}>
        <ScrollView
          ref={contentScrollRef}
          scrollEnabled={isDetails}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.content}
        >
          <CardSummaryTransition
            sheet={{ isDetails, progress, historyOpacity }}
            summary={{
              cardId: isBlocked ? undefined : cardId,
              balance,
              balanceKey,
              onToggleBalance: toggleGlobal,
            }}
          />
          <Column align="center" gap={24}>
            <CardCarousel
              cardTypes={cardTypes}
              issuedCards={issuedCards}
              selection={{ type: selectedType, lastDigits }}
              sheet={{ isDetails, historyOpacity, detailsOpacity }}
              carouselRef={carouselRef}
              actions={{
                onReturned: finishCardReturn,
                onSelect: selectCard,
                onOpen: openCard,
              }}
            />
            {isBlocked && isDetails && (
              <GText style={styles.blockedStatus}>
                {blockText(cardStatus === 'blocking' ? 'blocking' : 'blocked')}
              </GText>
            )}
            {freezeFlow.error && !freezeSheetVisible && isDetails && (
              <GText style={styles.blockedStatus}>{freezeText('error')}</GText>
            )}
          </Column>
          <Animated.View
            pointerEvents={isDetails ? 'auto' : 'none'}
            style={{
              opacity: detailsOpacity,
              transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [rh(24), 0] }) }],
            }}
          >
            <Reanimated.View key={selectedType} entering={CARD_CONTENT_ENTERING}>
              {cardId && !isBlocked && (
                <Column style={styles.balanceActions}>
                  <CardBalanceActions cardId={cardId} />
                </Column>
              )}
              {detailContent}
              {cardId && !isBlocked && (
                <CardDetailsActions
                  isDetails={isDetails}
                  detailsOpacity={detailsOpacity}
                  onBlock={() => setBlockSheetVisible(true)}
                  onFreeze={() => setFreezeSheetVisible(true)}
                  onPin={() => navigation.navigate(ScreensList.CARD_PIN_SETUP, { cardId })}
                  isFrozen={isFrozen}
                  isLoading={freezeFlow.isLoading}
                />
              )}
            </Reanimated.View>
          </Animated.View>
        </ScrollView>
        <OperationsPanel
          card={{ id: cardId ?? null, templateId: selectedType }}
          isDetails={isDetails}
          panelTop={panelTop}
          historyOpacity={historyOpacity}
          gestures={gestures}
          onClose={closeDetails}
        />
      </Flex>
      <CardStateSheets
        cardId={cardId}
        type={selectedType}
        status={cardStatus}
        block={{ isVisible: blockSheetVisible, onClose: () => setBlockSheetVisible(false) }}
        freeze={{ isVisible: freezeSheetVisible, flow: freezeFlow, onClose: () => setFreezeSheetVisible(false) }}
      />
    </ScreenWrapperWithoutScroll>
  )
}

type CardStateSheetsProps = {
  cardId?: string
  type: CardTemplateId
  status?: string
  block: { isVisible: boolean; onClose: () => void }
  freeze: { isVisible: boolean; flow: ReturnType<typeof useCardFreeze>; onClose: () => void }
}

function CardStateSheets({ cardId, type, status, block, freeze }: CardStateSheetsProps) {
  if (!cardId) return null
  return (
    <>
      <AppCardFreezeSheet
        key={`freeze-${cardId}`}
        isVisible={freeze.isVisible}
        state={{
          isFrozen: isFrozenCardStatus(status),
          isLoading: freeze.flow.isLoading,
          isError: freeze.flow.error,
        }}
        onClose={freeze.onClose}
        onConfirm={() => {
          void freeze.flow.confirm().then((success) => {
            if (success) freeze.onClose()
          })
        }}
      />
      <AppCardBlockSheet
        key={cardId}
        cardId={cardId}
        type={type}
        status={status}
        isVisible={block.isVisible}
        onClose={block.onClose}
      />
    </>
  )
}

type OperationsPanelProps = Pick<
  ReturnType<typeof useCardSheet>,
  'isDetails' | 'panelTop' | 'historyOpacity' | 'gestures'
> & { onClose: () => void; card: { id: string | null; templateId: CardTemplateId } }

function OperationsPanel({ isDetails, panelTop, historyOpacity, gestures, onClose, card }: OperationsPanelProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const expandedHandleHeight = Math.max(rh(44), 44)
  const operationsTitleOpacity = panelTop.interpolate({
    inputRange: [0, rh(100)],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  })
  const { panHandlers, headerPanHandlers, onHistoryScroll, onHistoryScrollEndDrag } = gestures
  return (
    <Animated.View style={[styles.panel, { top: panelTop }]} {...panHandlers}>
      <AnimatedPanelPressable
        testID="card-sheet-handle"
        accessibilityRole="button"
        accessibilityLabel={t('operations')}
        onPress={onClose}
        style={[
          styles.panelHeader,
          styles.dragHeader,
          {
            height: operationsTitleOpacity.interpolate({
              inputRange: [0, 1],
              outputRange: [rh(84), expandedHandleHeight],
            }),
            minHeight: operationsTitleOpacity.interpolate({
              inputRange: [0, 1],
              outputRange: [rh(84), expandedHandleHeight],
            }),
            paddingTop: operationsTitleOpacity.interpolate({ inputRange: [0, 1], outputRange: [rh(32), 0] }),
          },
        ]}
        {...headerPanHandlers}
      >
        <Column pointerEvents="none" align="center" style={styles.handleArea}>
          <Column style={styles.handle} />
        </Column>
        <Animated.View
          style={{ opacity: operationsTitleOpacity.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }) }}
        >
          <GText fw={600} style={styles.title}>
            {t('operations')}
          </GText>
        </Animated.View>
      </AnimatedPanelPressable>
      <Animated.View pointerEvents={isDetails ? 'none' : 'auto'} style={[styles.history, { opacity: historyOpacity }]}>
        <ScrollView
          key={card.templateId}
          testID="card-sheet-history"
          style={styles.historyScroll}
          contentContainerStyle={styles.operations}
          onScroll={onHistoryScroll}
          onScrollEndDrag={onHistoryScrollEndDrag}
          scrollEventThrottle={16}
        >
          <HomeOperations isPanel cardId={card.id} templateId={card.templateId} />
        </ScrollView>
      </Animated.View>
    </Animated.View>
  )
}
